import type en from '../en/admin'
import type { Translation } from '../types'

/** Platform administration */
const admin: Translation<typeof en> = {
  "admin.module.webhooks.label": "Webhooks",
  "admin.module.webhooks.desc": "Webhooks HTTP salientes ante eventos",
  "admin.module.branding.label": "Imagen de marca",
  "admin.module.branding.desc": "Logotipo y colores propios, marca blanca",
  "admin.module.apiKeys.label": "Claves de API",
  "admin.module.apiKeys.desc": "Acceso a la API para scripts y otros sistemas",
  "admin.module.templates.label": "Plantillas",
  "admin.module.templates.desc": "Plantillas y asistente de configuración de red",
  "admin.module.customDomain.label": "Dominio propio",
  "admin.module.customDomain.desc": "Inicio de sesión en la dirección propia de la empresa",
  "admin.module.customEmail.label": "Servidor de correo propio",
  "admin.module.customEmail.desc": "SMTP por empresa",
  "admin.module.sso.label": "Inicio de sesión único",
  "admin.module.sso.desc": "Inicio de sesión mediante el proveedor de identidad de la empresa (OpenID Connect)",
  "admin.module.ownWhatsapp.label": "WhatsApp propio",
  "admin.module.ownWhatsapp.desc": "La cuenta propia de Meta o Twilio de la empresa",
  "admin.module.ownRcs.label": "RCS propio",
  "admin.module.ownRcs.desc": "El remitente RCS propio de la empresa (Google o Twilio)",
  "admin.module.linkedWhatsapp.label": "Número de WhatsApp vinculado",
  "admin.module.linkedWhatsapp.desc": "La empresa vincula su propio número de WhatsApp al servidor (open-wa)",
  "admin.module.unifi.label": "UniFi",
  "admin.module.unifi.desc": "Controladores UniFi y Site Manager",
  "admin.module.teltonika.label": "Teltonika",
  "admin.module.teltonika.desc": "Dispositivos RutOS y RMS",
  "admin.module.peplink.label": "Peplink",
  "admin.module.peplink.desc": "Dispositivos Peplink e InControl2",
  "admin.module.mikrotik.label": "MikroTik",
  "admin.module.mikrotik.desc": "Routers RouterOS, puntos de acceso y enlaces de radio",
  "admin.module.velanox.label": "Velanox One Speed",
  "admin.module.velanox.desc": "Equipos y endpoints Velanox",
  "admin.overview.title": "Resumen de la plataforma",
  "admin.overview.subtitle": "Panel de la administración de la plataforma",
  "admin.overview.companies": "Empresas",
  "admin.overview.users": "Usuarios",
  "admin.overview.sites": "Sitios",
  "admin.overview.pendingJobs": "Tareas pendientes",
  "admin.overview.revocations": "Revocaciones de certificados en reintento",
  "admin.overview.revocationsText": "Estos certificados VPN aún no se han podido revocar, así que siguen funcionando. Cada uno se reintenta (como máximo cada hora) hasta que se consigue.",
  "admin.overview.siteCert": "Certificado de sitio",
  "admin.overview.userCert": "Certificado de usuario",
  "admin.overview.tries": { one: "{count} intento · próximo intento {when}", other: "{count} intentos · próximo intento {when}" },
  "admin.overview.byStatus": "Empresas por estado",
  "admin.overview.jobs": "Cola de tareas",
  "admin.overview.all": "Todas",
  "admin.overview.refreshJobs": "Actualizar tareas",
  "admin.overview.noJobs": "No hay tareas",
  "admin.overview.audit": "Registro de auditoría reciente",
  "admin.overview.noAudit": "No hay entradas en el registro de auditoría",
  "admin.overview.system": "Sistema",
  "admin.vpn.title": "Gestión de la VPN",
  "admin.vpn.subtitle": "Servidores OpenVPN, redes y cuentas de cada empresa",
  "admin.vpn.refresh": "Actualizar",
  "admin.vpn.server": "Servidor",
  "admin.vpn.running": "En marcha",
  "admin.vpn.stopped": "Detenido",
  "admin.vpn.since": "Desde {when}",
  "admin.vpn.connectedNow": "Conectados ahora",
  "admin.vpn.ofActive": { one: "de {count} cuenta activa", other: "de {count} cuentas activas" },
  "admin.vpn.totalAccounts": "Cuentas en total",
  "admin.vpn.acrossCompanies": { one: "en {count} empresa", other: "en {count} empresas" },
  "admin.vpn.networks": "Redes",
  "admin.vpn.isolated": "subredes /24 separadas",
  "admin.vpn.currentlyConnected": "Conectados ahora ({count})",
  "admin.vpn.commonName": "Nombre común",
  "admin.vpn.realIp": "IP real",
  "admin.vpn.sinceCol": "Desde",
  "admin.vpn.usersSites": "Usuarios: {users} · Sitios: {sites}",
  "admin.vpn.connectedOf": { one: "{connected} conectada(s) / {count} cuenta", other: "{connected} conectada(s) / {count} cuentas" },
  "admin.vpn.noAccounts": "Todavía no hay cuentas VPN",
  "admin.vpn.name": "Nombre",
  "admin.vpn.logs": "Registros de OpenVPN",
  "admin.vpn.logLines": "Líneas del registro",
  "admin.vpn.lines": { one: "{count} línea", other: "{count} líneas" },
  "admin.vpn.refreshLogs": "Actualizar registros",
  "admin.vpn.hide": "Ocultar",
  "admin.vpn.showLogs": "Mostrar registros",
  "admin.vpn.loadHint": "Elija «Mostrar registros» para cargarlos",
  "admin.plans.toggleFailed": "Sin cambios",
  "admin.plans.deleted": "Plan eliminado",
  "admin.plans.deleteFailed": "No se puede eliminar el plan: puede tener licencias activas",
  "admin.plans.title": "Planes de licencia",
  "admin.plans.subtitle": "Tipos de licencia reutilizables y los módulos que desbloquean",
  "admin.plans.new": "Nuevo plan",
  "admin.plans.deleteConfirm": "¿Eliminar el plan «{name}»? Las empresas que lo usan conservan su licencia actual.",
  "admin.plans.empty": "Todavía no hay planes de licencia: cree uno para asignarlo a empresas",
  "admin.plans.createTitle": "Crear plan de licencia",
  "admin.plans.created": "Plan creado",
  "admin.plans.editTitle": "Editar: {name}",
  "admin.plans.updated": "Plan actualizado",
  "admin.plans.public": "Público",
  "admin.plans.activeCount": { one: "{count} activa", other: "{count} activas" },
  "admin.plans.editNamed": "Editar {name}",
  "admin.plans.deleteNamed": "Eliminar {name}",
  "admin.plans.sites": "Sitios",
  "admin.plans.users": "Usuarios",
  "admin.plans.duration": "Duración",
  "admin.plans.daysShort": "{count} d",
  "admin.plans.modules": "Módulos",
  "admin.plans.nameRequired": "El nombre es obligatorio",
  "admin.plans.saveFailed": "No se pudo guardar el plan",
  "admin.plans.name": "Nombre del plan *",
  "admin.plans.namePlaceholder": "p. ej., Profesional",
  "admin.plans.description": "Descripción",
  "admin.plans.descriptionPlaceholder": "Descripción opcional",
  "admin.plans.limits": "Límites",
  "admin.plans.limitsHint": "(déjelo vacío para sin límite)",
  "admin.plans.maxSites": "Máximo de sitios",
  "admin.plans.maxUsers": "Máximo de usuarios",
  "admin.plans.durationDays": "Duración (días)",
  "admin.plans.included": "Módulos incluidos",
  "admin.plans.selfSignup": "Disponible para el registro propio",
  "admin.plans.selfSignupHint": "Mostrar este plan en la página de registro",
  "admin.plans.save": "Guardar plan",
  "admin.admins.removed": "Administrador global eliminado",
  "admin.admins.removeFailed": "Error al quitar",
  "admin.admins.reset2faDone": "Inicio de sesión en dos pasos restablecido",
  "admin.admins.reset2faText": "La persona cierra sesión en todas partes y lo vuelve a configurar al iniciar sesión.",
  "admin.admins.resetFailed": "Error al restablecer",
  "admin.admins.title": "Administradores globales",
  "admin.admins.subtitle": "Personas con derechos de administrador global: administración de la plataforma para todas las empresas",
  "admin.admins.add": "Añadir administrador global",
  "admin.admins.warning": "Los administradores globales tienen acceso completo a todas las empresas, ajustes y datos de la plataforma. Concédalo solo a personal de confianza.",
  "admin.admins.name": "Nombre",
  "admin.admins.email": "Correo electrónico",
  "admin.admins.role": "Función",
  "admin.admins.lastLogin": "Último inicio de sesión",
  "admin.admins.created": "Creado",
  "admin.admins.empty": "No se han encontrado administradores globales",
  "admin.admins.you": "(usted)",
  "admin.admins.owner": "Propietario",
  "admin.admins.superAdmin": "Superadministrador",
  "admin.admins.admin": "Administrador",
  "admin.admins.invited": "Invitado",
  "admin.admins.never": "Nunca",
  "admin.admins.edit": "Editar",
  "admin.admins.editNamed": "Editar a {name}",
  "admin.admins.reset2fa": "Restablecer el inicio de sesión en dos pasos",
  "admin.admins.reset2faFor": "Restablecer el inicio de sesión en dos pasos de {name}",
  "admin.admins.reset2faConfirm": "¿Restablecer el inicio de sesión en dos pasos de «{name}»? La persona cierra sesión en todas partes y lo vuelve a configurar al iniciar sesión.",
  "admin.admins.ownerFixed": "El propietario de la plataforma no se puede quitar",
  "admin.admins.notSelf": "No puede quitarse sus propios derechos",
  "admin.admins.remove": "Quitar",
  "admin.admins.removeNamed": "Quitar a {name} como administrador global",
  "admin.admins.removeConfirm": "¿Quitar a «{name}» ({email}) como administrador global? Una cuenta que pertenece a empresas las conserva; una sin empresas se elimina.",
  "admin.admins.ownHint": "Su propio nombre, contraseña e inicio de sesión en dos pasos están en",
  "admin.admins.invitationSent": "Invitación enviada",
  "admin.admins.invitationText": "{email} establece una contraseña desde el correo y después el inicio de sesión en dos pasos.",
  "admin.admins.added": "Administrador global añadido",
  "admin.admins.addedText": "Esta cuenta tiene ahora derechos de administrador global.",
  "admin.admins.editTitle": "Editar: {name}",
  "admin.admins.updated": "Administrador actualizado",
  "admin.admins.required": "El nombre y el correo electrónico son obligatorios",
  "admin.admins.saveFailed": "Error al guardar",
  "admin.admins.fullName": "Nombre completo *",
  "admin.admins.emailAddress": "Correo electrónico *",
  "admin.admins.emailAddressPlain": "Correo electrónico",
  "admin.admins.emailHint": "Una cuenta existente recibe los derechos. Una dirección nueva recibe un correo para establecer una contraseña.",
  "admin.admins.emailFixed": "El correo electrónico no se puede cambiar",
  "admin.admins.superAdminHint": "Puede gestionar otros administradores globales y los ajustes críticos de la plataforma",
  "admin.admins.saveChanges": "Guardar cambios",
  "admin.admins.sendInvitation": "Enviar invitación",
  "admin.settings.provider": "Proveedor",
  "admin.settings.serverSettings": "Usar la configuración del servidor (.env)",
  "admin.settings.off": "Desactivado",
  "admin.settings.waHint": "En cuanto se elige aquí Meta o Twilio, solo cuentan los valores de esta página: no se toma nada de la configuración del servidor. Los cambios guardados se aplican de inmediato, sin reiniciar. Los tokens se guardan cifrados y no se vuelven a mostrar. Las empresas con el módulo de licencia WhatsApp propio pueden usar su propia conexión (Configuración de la empresa → WhatsApp).",
  "admin.settings.changedProvider": "Ha cambiado de proveedor: guarde la configuración para usarlo.",
  "admin.settings.rcsHint": "Los mensajes RCS llegan a la aplicación de mensajes del propio teléfono, con su nombre de empresa verificado. En cuanto se elige aquí Google o Twilio, solo cuentan los valores de esta página. Los cambios guardados se aplican de inmediato. Las claves y los tokens se guardan cifrados y no se vuelven a mostrar. Las empresas con el módulo de licencia RCS propio pueden usar su propio remitente (Configuración de la empresa → RCS).",
  "admin.settings.templatesChecked": "Plantillas comprobadas",
  "admin.settings.syncFailed": "Error de sincronización",
  "admin.settings.tryAgain": "Vuelva a intentarlo en un momento.",
  "admin.settings.templatesOff": "Cuando WhatsApp pasa por Twilio (elegido arriba o en la configuración del servidor), la plataforma crea las plantillas y las empresas pueden cambiar sus textos. Con Meta se usan las plantillas indicadas en su configuración.",
  "admin.settings.templatesIntro": "WhatsApp aprueba cada texto antes de que pueda usarse. Los textos nuevos se envían y las decisiones se consultan cada 30 minutos; hasta que se aprueba el texto propio de una empresa, se envía el predeterminado.",
  "admin.settings.templatesInUse": { one: "{count} plantilla en uso. WhatsApp permite {unverified} por cuenta de empresa, o {verified} una vez verificada la empresa con Meta. El texto de WhatsApp propio de cada empresa es una plantilla independiente.", other: "{count} plantillas en uso. WhatsApp permite {unverified} por cuenta de empresa, o {verified} una vez verificada la empresa con Meta. El texto de WhatsApp propio de cada empresa es una plantilla independiente." },
  "admin.settings.nearLimit": "Cerca del límite de una cuenta de empresa no verificada ({limit}). Verifique la empresa con Meta o no se podrán aprobar textos nuevos cuando se alcance.",
  "admin.settings.message": "Mensaje",
  "admin.settings.text": "Texto",
  "admin.settings.usedBy": "Usada por",
  "admin.settings.authMessage": "El mensaje de código de verificación propio de WhatsApp",
  "admin.settings.usedBy.default": "Predeterminado",
  "admin.settings.usedBy.previousDefault": "Texto predeterminado anterior, hasta que se apruebe el nuevo",
  "admin.settings.notUsed": "Ya no se usa",
  "admin.settings.wa.approved": "aprobada",
  "admin.settings.wa.rejected": "rechazada",
  "admin.settings.wa.failed": "no enviada",
  "admin.settings.wa.paused": "en pausa",
  "admin.settings.wa.disabled": "desactivada",
  "admin.settings.wa.pending": "en espera",
  "admin.settings.noTemplates": "Ninguna todavía: la primera sincronización las crea.",
  "admin.settings.syncNow": "Sincronizar ahora",
  "admin.settings.testSent": "Correo de prueba enviado",
  "admin.settings.checkInbox": "Revise {to}",
  "admin.settings.smtpFailed": "Error en la prueba SMTP",
  "admin.settings.save": "Guardar configuración",
  "admin.settings.title": "Configuración de la plataforma",
  "admin.settings.subtitle": "Comportamiento general, correo, WhatsApp, RCS y VPN",
  "admin.settings.tab": "Configuración",
  "admin.settings.edgeTab": "Proxy perimetral (Caddy + HAProxy) →",
  "admin.settings.loading": "Cargando",
  "admin.settings.behaviour": "Comportamiento de la plataforma",
  "admin.settings.licensing": "Licencias activadas",
  "admin.settings.licensingHint": "Aplicar los límites de licencia. Desactívelo para un acceso ilimitado.",
  "admin.settings.maintenance": "Modo de mantenimiento",
  "admin.settings.maintenanceHint": "Impide que los usuarios de las empresas inicien sesión. Los administradores globales mantienen el acceso.",
  "admin.settings.selfRegistration": "Permitir el registro propio",
  "admin.settings.selfRegistrationHint": "Permite que las empresas se registren sin invitación de un administrador global.",
  "admin.settings.adminIdle": "Administradores globales: cerrar sesión tras un tiempo sin actividad, como máximo",
  "admin.settings.adminIdleHint": "1 hora por defecto; cada administrador puede elegir un tiempo menor en Mi perfil.",
  "admin.settings.idleOff": "Los administradores pueden desactivar el cierre de sesión por inactividad",
  "admin.settings.idleOffHint": "Para su propia cuenta.",
  "admin.settings.idleOffWarning": "Esto permite que los administradores globales desactiven una de las protecciones de cuenta de la plataforma: una sesión de administrador abierta en un ordenador desatendido sigue iniciada hasta alcanzar su duración máxima.",
  "admin.settings.idleOffQuestion": "¿Permitirlo?",
  "admin.settings.defaultLicense": "Tipo de licencia predeterminado",
  "admin.settings.licenseType.TRIAL": "Prueba",
  "admin.settings.licenseType.STANDARD": "Estándar",
  "admin.settings.licenseType.UNLIMITED": "Ilimitada",
  "admin.settings.licenseType.CUSTOM": "Personalizada",
  "admin.settings.trialDays": "Días de prueba predeterminados",
  "admin.settings.email": "Correo / SMTP",
  "admin.settings.emailIntro": "Valores predeterminados para toda la plataforma. Las empresas pueden usar su propio SMTP en su configuración. Las contraseñas se cifran con AES-256 antes de guardarse.",
  "admin.settings.smtpHost": "Host SMTP",
  "admin.settings.smtpPort": "Puerto SMTP",
  "admin.settings.username": "Usuario",
  "admin.settings.password": "Contraseña",
  "admin.settings.saved": "✓ guardada",
  "admin.settings.keepPassword": "(déjelo vacío para conservar la actual)",
  "admin.settings.enterPassword": "Contraseña SMTP",
  "admin.settings.hidePassword": "Ocultar contraseña",
  "admin.settings.showPassword": "Mostrar contraseña",
  "admin.settings.fromEmail": "Correo del remitente",
  "admin.settings.fromName": "Nombre del remitente",
  "admin.settings.sendTest": "Enviar correo de prueba",
  "admin.settings.testRecipient": "Destinatario de la prueba",
  "admin.settings.testRecipientPlaceholder": "su@correo.es",
  "admin.settings.send": "Enviar prueba",
  "admin.settings.waTemplates": "Plantillas de WhatsApp",
  "admin.settings.vpn": "VPN / OpenVPN",
  "admin.settings.vpnIntro": "Controla la VPN de las empresas nuevas. Cada empresa recibe su propia subred /24.",
  "admin.settings.serverHost": "Nombre de host / IP del servidor",
  "admin.settings.usersBase": "Base de la red de usuarios",
  "admin.settings.sitesBase": "Base de la red de sitios",
  "admin.settings.usersExample": "Empresa 1 → 10.8.1.0/24 · Empresa 2 → 10.8.2.0/24 …",
  "admin.settings.sitesExample": "Empresa 1 → 10.9.1.0/24 · Empresa 2 → 10.9.2.0/24 …",
  "admin.licences.deleted": "Licencia eliminada",
  "admin.licences.deleteFailed": "Error al eliminar",
  "admin.licences.notified": "Correo enviado a los administradores de la empresa",
  "admin.licences.notifyFailed": "No se pudo enviar el correo",
  "admin.licences.keyRevoked": "Clave revocada",
  "admin.licences.revokeFailed": "Error al revocar",
  "admin.licences.unusedDeleted": "Claves sin usar eliminadas",
  "admin.licences.expiredDeleted": "Licencias caducadas eliminadas",
  "admin.licences.bulkFailed": "Error al eliminar",
  "admin.licences.tab.used": "Activas",
  "admin.licences.tab.available": "Claves",
  "admin.licences.tab.expired": "Caducadas",
  "admin.licences.title": "Resumen de licencias",
  "admin.licences.subtitle": "Todas las licencias y claves emitidas en la plataforma",
  "admin.licences.generate": "Generar clave",
  "admin.licences.activeCount": "Licencias activas",
  "admin.licences.availableCount": "Claves disponibles",
  "admin.licences.expiredCount": "Licencias caducadas",
  "admin.licences.totalKeys": "Claves emitidas en total",
  "admin.licences.company": "Empresa",
  "admin.licences.plan": "Plan",
  "admin.licences.started": "Inicio",
  "admin.licences.expires": "Caduca",
  "admin.licences.limits": "Límites",
  "admin.licences.noActive": "No hay licencias activas",
  "admin.licences.daysLeft": { one: "queda {count} día", other: "quedan {count} días" },
  "admin.licences.unlimited": "Ilimitada",
  "admin.licences.sites": { one: "{count} sitio", other: "{count} sitios" },
  "admin.licences.sitesUnlimited": "∞ sitios",
  "admin.licences.users": { one: "{count} usuario", other: "{count} usuarios" },
  "admin.licences.usersUnlimited": "∞ usuarios",
  "admin.licences.thisCompany": "esta empresa",
  "admin.licences.deleteFor": "Eliminar la licencia de {name}",
  "admin.licences.deleteConfirm": "¿Eliminar la licencia de «{name}»?",
  "admin.licences.deleteUnusedConfirm": { one: "¿Eliminar la clave sin usar?", other: "¿Eliminar las {count} claves sin usar?" },
  "admin.licences.deleteUnused": "Eliminar todas las sin usar",
  "admin.licences.available": "Disponibles ({count})",
  "admin.licences.key": "Clave",
  "admin.licences.restriction": "Restricción",
  "admin.licences.created": "Creada",
  "admin.licences.redeemed": "Canjeadas ({count})",
  "admin.licences.usedBy": "Usada por",
  "admin.licences.redeemedAt": "Canjeada",
  "admin.licences.noKeys": "Todavía no hay claves de licencia: genérelas arriba",
  "admin.licences.deleteExpiredConfirm": { one: "¿Eliminar la licencia caducada?", other: "¿Eliminar las {count} licencias caducadas?" },
  "admin.licences.deleteExpired": "Eliminar todas las caducadas",
  "admin.licences.expiredOn": "Caducó",
  "admin.licences.durationWas": "Duración",
  "admin.licences.noExpired": "No hay licencias caducadas",
  "admin.licences.days": { one: "{count} día", other: "{count} días" },
  "admin.licences.notifyHint": "Avisar por correo a los administradores de la empresa de la caducidad",
  "admin.licences.notify": "Avisar",
  "admin.licences.deleteExpiredFor": "Eliminar la licencia caducada de {name}",
  "admin.licences.deleteExpiredOne": "¿Eliminar la licencia caducada de «{name}»?",
  "admin.licences.copyKey": "Copiar la clave completa",
  "admin.licences.anyPlan": "Cualquiera",
  "admin.licences.companyOnly": "🏢 Solo una empresa",
  "admin.licences.noRestriction": "🌐 Ilimitada",
  "admin.licences.never": "Nunca",
  "admin.licences.revokeKey": "Revocar clave",
  "admin.licences.revokeConfirm": "¿Revocar esta clave?",
  "admin.licences.generated": { one: "{count} clave generada", other: "{count} claves generadas" },
  "admin.licences.generateFailed": "Error al generar",
  "admin.licences.generateTitle": { one: "Generar clave de licencia", other: "Generar claves de licencia" },
  "admin.licences.sendKeys": "Copie estas claves y envíelas a sus clientes. Pueden canjearlas al registrarse o en la configuración de su cuenta.",
  "admin.licences.copy": "Copiar",
  "admin.licences.allCopied": "Todas las claves copiadas",
  "admin.licences.copyAll": "Copiar todas",
  "admin.licences.done": "Listo",
  "admin.licences.licencePlan": "Plan de licencia",
  "admin.licences.anyPlanOption": "Cualquier plan (sin restricción)",
  "admin.licences.restrict.unlimited": "🌐 Ilimitada",
  "admin.licences.restrict.email": "📧 Correo",
  "admin.licences.restrict.company": "🏢 Empresa",
  "admin.licences.limitEmail": "Limitar al correo",
  "admin.licences.limitCompany": "Limitar a la empresa",
  "admin.licences.chooseCompany": "Elija una empresa…",
  "admin.licences.count": "Número de claves",
  "admin.licences.keyExpires": "La clave caduca",
  "admin.licences.optional": "(opcional)",
  "admin.licences.notes": "Notas",
  "admin.licences.notesPlaceholder": "Nota interna sobre este lote de claves",
  "admin.licences.generateCount": { one: "Generar clave", other: "Generar {count} claves" },
  "admin.edge.resolves": "{domain} resuelve correctamente",
  "admin.edge.dnsNotHere": "El DNS todavía no apunta aquí",
  "admin.edge.resolvesTo": "Resuelve a {ips}",
  "admin.edge.noARecord": "No se encontró ningún registro A",
  "admin.edge.proven": "Propiedad demostrada",
  "admin.edge.servesNow": "El dominio ya muestra el panel.",
  "admin.edge.notProvenYet": "Todavía no demostrada",
  "admin.edge.markedProven": "Marcada como demostrada",
  "admin.edge.ownershipReset": "Propiedad restablecida",
  "admin.edge.resetText": "El dominio muestra la página de configuración hasta que se encuentre el nuevo registro TXT.",
  "admin.edge.failed": "Error",
  "admin.edge.requestFailed": "La solicitud falló",
  "admin.edge.removed": "Dominio eliminado",
  "admin.edge.caddyApplied": "Configuración de Caddy aplicada",
  "admin.edge.caddyRejected": "Caddy rechazó la configuración",
  "admin.edge.title": "Proxy perimetral",
  "admin.edge.subtitle": "HAProxy delante de :80/:443 · Caddy termina el TLS y emite certificados bajo demanda · OpenVPN por empresa sobre UDP",
  "admin.edge.addDomain": "Añadir dominio",
  "admin.edge.connections": { one: "{count} conexión", other: "{count} conexiones" },
  "admin.edge.notReachable": "No accesible",
  "admin.edge.onDemandOn": "TLS bajo demanda activo",
  "admin.edge.onDemandOff": "TLS bajo demanda NO configurado",
  "admin.edge.edgeIp": "IP perimetral",
  "admin.edge.noEdgeIp": "Sin definir: no se puede indicar a los clientes adónde apuntar su DNS",
  "admin.edge.acme": "ACME: {email}",
  "admin.edge.noAcme": "No hay correo ACME definido",
  "admin.edge.caddyWarnBefore": "Caddy está en marcha, pero el TLS bajo demanda no está vinculado al endpoint ask. Los dominios de marca blanca no obtendrán certificados hasta que aplique la configuración: use ",
  "admin.edge.caddyWarnAfter": " en Configuración.",
  "admin.edge.tab.domains": "Dominios ({count})",
  "admin.edge.tab.vpn": "Instancias VPN",
  "admin.edge.tab.config": "Configuración",
  "admin.edge.domain": "Dominio",
  "admin.edge.type": "Tipo",
  "admin.edge.company": "Empresa",
  "admin.edge.dns": "DNS",
  "admin.edge.ownership": "Propiedad",
  "admin.edge.certificate": "Certificado",
  "admin.edge.noDomains": "Todavía no hay dominios",
  "admin.edge.typeName.WHITELABEL": "Marca blanca",
  "admin.edge.typeName.PLATFORM": "Plataforma",
  "admin.edge.typeName.APP": "Otra aplicación",
  "admin.edge.typeName.REDIRECT": "Redirección",
  "admin.edge.typeName.VPN": "VPN",
  "admin.edge.typeDesc.WHITELABEL": "El dominio propio del panel de un cliente",
  "admin.edge.typeDesc.PLATFORM": "Su propio nombre de host para el panel",
  "admin.edge.typeDesc.APP": "Otra aplicación web en este servidor",
  "admin.edge.typeDesc.REDIRECT": "Envía este nombre a otro",
  "admin.edge.dnsResolves": "resuelve",
  "admin.edge.awaitingDns": "esperando el DNS",
  "admin.edge.provenBadge": "demostrada",
  "admin.edge.provenBy": "por {name}",
  "admin.edge.anAdmin": "un administrador",
  "admin.edge.byDns": "mediante registro DNS",
  "admin.edge.notProven": "no demostrada",
  "admin.edge.certFailed": "error",
  "admin.edge.certOnFirst": "se emite en la primera solicitud",
  "admin.edge.certAuto": "solicitado automáticamente",
  "admin.edge.pendingDns": "esperando el DNS",
  "admin.edge.recheck": "Volver a comprobar el DNS",
  "admin.edge.resetOwnership": "Restablecer la propiedad",
  "admin.edge.resetConfirm": "¿Restablecer la propiedad de {domain}? Mostrará la página «en configuración» hasta que la empresa publique el nuevo registro TXT.",
  "admin.edge.remove": "Eliminar",
  "admin.edge.removeWhitelabel": "¿Eliminar {domain}? Sus configuraciones VPN vuelven al nombre de host de la plataforma.",
  "admin.edge.removeOther": "¿Eliminar {domain}? Este servidor dejará de responder por él.",
  "admin.edge.txtRecord": "Registro de propiedad (TXT):",
  "admin.edge.copyRecord": "Copiar el valor del registro de {domain}",
  "admin.edge.releasedOn": "Se libera el {date} salvo que se demuestre",
  "admin.edge.checkNow": "Comprobar ahora",
  "admin.edge.overrideConfirm": "¿Marcar {domain} como demostrada sin su registro TXT? Empezará a mostrar de inmediato el panel de {company}.",
  "admin.edge.theCompany": "la empresa",
  "admin.edge.markProven": "Marcar como demostrada",
  "admin.edge.vpnIntroBefore": "Dos servidores OpenVPN por empresa (usuarios y sitios), cada uno en su propio puerto UDP. Un reinicio o una configuración errónea solo afecta a esa empresa. Las empresas de marca blanca necesitan un registro A ",
  "admin.edge.vpnIntroAfter": " que apunte a la IP perimetral.",
  "admin.edge.theirDomain": "su-dominio",
  "admin.edge.unit": "Unidad",
  "admin.edge.udpPorts": "Puertos UDP",
  "admin.edge.connectTo": "Los clientes se conectan a",
  "admin.edge.state": "Estado",
  "admin.edge.noInstances": "No hay instancias VPN aprovisionadas",
  "admin.edge.whitelabelHost": "marca blanca: {domain}",
  "admin.edge.usersPort": "usuarios {port}",
  "admin.edge.sitesPort": "sitios {port}",
  "admin.edge.repoint": "Volver a apuntar al dominio actual",
  "admin.edge.hostRefreshed": "Host remoto actualizado",
  "admin.edge.reprovision": "Volver a aprovisionar",
  "admin.edge.vpnRunning": "Servidores VPN en marcha",
  "admin.edge.queued": { one: "{count} cuenta que no se pudo crear antes está en cola", other: "{count} cuentas que no se pudieron crear antes están en cola" },
  "admin.edge.vpnNotRunning": "Los servidores VPN no están en marcha",
  "admin.edge.provisionFailed": "Error al aprovisionar",
  "admin.edge.stop": "Detener",
  "admin.edge.stopConfirm": "¿Detener la VPN de {company}? Sus túneles se cortarán.",
  "admin.edge.attention": "Requiere atención",
  "admin.edge.healthy": "Correcto",
  "admin.edge.unreachable": "Inaccesible",
  "admin.edge.caddyText": "Envía la configuración generada por la API de administración. Los dominios de la plataforma aparecen de forma explícita; los de marca blanca usan TLS bajo demanda controlado por el endpoint ask, así que añadir uno no requiere recargar.",
  "admin.edge.applyCaddy": "Aplicar la configuración de Caddy",
  "admin.edge.haproxyText1": "La configura el instalador y solo cambia cuando cambia la propia arquitectura perimetral. La configuración que debería tener está abajo: para aplicarla, cópiela en ",
  "admin.edge.haproxyText2": " en el servidor, compruébela con ",
  "admin.edge.haproxyText3": ", muévala sobre ",
  "admin.edge.haproxyText4": " y ejecute ",
  "admin.edge.haproxyText5": ". El panel no la escribe: la API se ejecuta sin root.",
  "admin.edge.generatedCfg": "haproxy.cfg generada",
  "admin.edge.copy": "Copiar",
  "admin.edge.registered": "Dominio registrado",
  "admin.edge.addFailed": "No se pudo añadir el dominio",
  "admin.edge.registeredTitle": "{domain} registrado",
  "admin.edge.currentlyResolves": "Resuelve actualmente a: {ips}",
  "admin.edge.done": "Listo",
  "admin.edge.domainLabel": "Dominio *",
  "admin.edge.pointA": "Haga que un registro A apunte a ",
  "admin.edge.certSoon": " El certificado se solicita en cuanto el nombre resuelva aquí.",
  "admin.edge.certFirst": " El certificado se emite automáticamente en la primera solicitud.",
  "admin.edge.pointASuffix": ".",
  "admin.edge.portLabel": "Puerto en este servidor *",
  "admin.edge.portHint": "https://{domain} va a http://127.0.0.1:{port}, sin nada del panel entre medias. Se rechazan los puertos que usan HAProxy, Caddy o el panel.",
  "admin.edge.redirectLabel": "Redirigir a *",
  "admin.edge.redirectHint": "Un nombre de host. Cada solicitud va allí por HTTPS con su propia ruta y consulta, como redirección permanente.",
  "admin.edge.companyLabel": "Empresa *",
  "admin.edge.chooseCompany": "Elija una empresa…",
  "admin.edge.vpnSwitchBefore": "Sus configuraciones VPN cambian automáticamente a ",
  "admin.edge.vpnSwitchAfter": ".",
  "admin.edge.step.prove": "Demuestre que {domain} es suyo: añada un registro TXT llamado {name} con el valor {value}. Hasta entonces, {domain} muestra una página «en configuración».",
  "admin.edge.step.pointAt": "Haga que un registro A de {domain} apunte a {ip} y vuelva a comprobarlo.",
  "admin.edge.step.pointHere": "Haga que un registro A de {domain} apunte a este servidor y vuelva a comprobarlo. Defina la IP pública perimetral en la configuración de la plataforma para que se compruebe automáticamente.",
  "admin.edge.step.appOk": "El DNS es correcto: https://{domain} va ahora a la aplicación del puerto {port} de este servidor y se está solicitando su certificado.",
  "admin.edge.step.redirectOk": "El DNS es correcto: https://{domain} redirige ahora a https://{target} y se está solicitando su certificado.",
  "admin.edge.step.dnsOk": "El DNS es correcto. Caddy emite un certificado en la primera solicitud a https://{domain}.",
  "admin.edge.step.vpnRecord": "Cree un registro A para {host} que apunte a {ip}: los clientes VPN se conectan allí.",
  "admin.edge.thisServer": "este servidor",
  "admin.edge.step.vpnHost": "Las configuraciones VPN de esta empresa usan ahora {host}. Los archivos .ovpn existentes siguen funcionando mientras su antiguo nombre de host resuelva aquí; descárguelos de nuevo para cambiar.",
  "admin.companies.title": "Empresas",
  "admin.companies.count": { one: "{count} empresa en la plataforma", other: "{count} empresas en la plataforma" },
  "admin.companies.create": "Crear empresa",
  "admin.companies.search": "Buscar empresas",
  "admin.companies.searchPlaceholder": "Buscar por nombre o slug",
  "admin.companies.company": "Empresa",
  "admin.companies.status": "Estado",
  "admin.companies.licence": "Licencia",
  "admin.companies.sites": "Sitios",
  "admin.companies.users": "Usuarios",
  "admin.companies.created": "Creada",
  "admin.companies.expiresOn": "Caduca el {date}",
  "admin.companies.edit": "Editar empresa",
  "admin.companies.settings": "Configuración de la empresa",
  "admin.companies.usersInvites": "Usuarios e invitaciones",
  "admin.companies.manageLicence": "Gestionar licencia",
  "admin.companies.assignAdmin": "Asignar administrador",
  "admin.companies.delete": "Eliminar empresa",
  "admin.companies.createFailed": "No se pudo crear la empresa",
  "admin.companies.nameLabel": "Nombre de la empresa *",
  "admin.companies.slugLabel": "Slug *",
  "admin.companies.adminUser": "Administrador",
  "admin.companies.adminName": "Nombre del administrador *",
  "admin.companies.adminEmail": "Correo del administrador *",
  "admin.companies.licenceTypeLabel": "Tipo de licencia",
  "admin.companies.trial30": "Prueba (30 días)",
  "admin.companies.expiryDate": "Fecha de caducidad",
  "admin.companies.maxSites": "Sitios máx.",
  "admin.companies.maxUsers": "Usuarios máx.",
  "admin.companies.unlimited": "Ilimitado",
  "admin.companies.licenceUpdated": "Licencia actualizada",
  "admin.companies.saveFailed": "Error al guardar",
  "admin.companies.assignLicence": "Asignar licencia",
  "admin.companies.fromPlan": "📋 Desde un plan",
  "admin.companies.manual": "✏️ A mano",
  "admin.companies.licencePlan": "Plan de licencia",
  "admin.companies.noPlansBefore": "No hay planes de licencia activos: cree primero uno en ",
  "admin.companies.noPlansLink": "Planes de licencia",
  "admin.companies.noPlansAfter": ".",
  "admin.companies.blankUnlimited": "(vacío = ilimitada)",
  "admin.companies.modules": "Módulos",
  "admin.companies.modulesHint": "Definidos a mano, sustituyen a los de un plan: al guardar, la licencia se separa de su plan. Elegir un plan más adelante vuelve a usar los módulos del plan.",
  "admin.companies.notes": "Notas",
  "admin.companies.internalNote": "Nota interna",
  "admin.companies.inviteSent": "Invitación enviada",
  "admin.companies.inviteResent": "La invitación se ha reenviado al usuario.",
  "admin.companies.resendFailed": "No se pudo reenviar la invitación",
  "admin.companies.twoFactorReset": "Inicio de sesión en dos pasos restablecido",
  "admin.companies.twoFactorResetText": "Se cierra su sesión en todas partes y lo configura de nuevo la próxima vez que inicie sesión.",
  "admin.companies.resetFailed": "Error al restablecer",
  "admin.companies.usersTitle": "Usuarios de la empresa",
  "admin.companies.noUsers": "No hay usuarios en esta empresa",
  "admin.companies.twoFactorOn": "El inicio de sesión en dos pasos está activado",
  "admin.companies.resendInvite": "Reenviar la invitación",
  "admin.companies.resend": "Reenviar",
  "admin.companies.reset2fa": "Restablecer el inicio de sesión en dos pasos",
  "admin.companies.reset2faFor": "Restablecer el inicio de sesión en dos pasos de {name}",
  "admin.companies.reset2faConfirm": "¿Restablecer el inicio de sesión en dos pasos de «{name}»? Se cerrará su sesión en todas partes y lo configurará de nuevo la próxima vez que inicie sesión.",
  "admin.companies.updateFailed": "No se pudo actualizar la empresa",
  "admin.companies.domainPlaceholder": "p. ej., portal.acme.com",
  "admin.companies.statusName.PENDING_SETUP": "En configuración",
  "admin.companies.statusName.ACTIVE": "Activa",
  "admin.companies.statusName.SUSPENDED": "Suspendida",
  "admin.companies.statusName.CANCELLED": "Cancelada",
  "admin.companies.saveChanges": "Guardar cambios",
  "admin.companies.notSaved": "Configuración no guardada",
  "admin.companies.tryAgain": "Vuelva a intentarlo en un momento.",
  "admin.companies.monitoring": "Supervisión",
  "admin.companies.timezone": "Zona horaria",
  "admin.companies.language": "Idioma",
  "admin.companies.ownMail": "Servidor de correo propio (en lugar del de la plataforma)",
  "admin.companies.mailInUse": "En uso: el servidor de correo propio de la empresa.",
  "admin.companies.mailNotYet": "Todavía no se usa: hasta que el administrador de la empresa envíe un correo de prueba desde Configuración de la empresa, su correo pasa por el servidor de la plataforma.",
  "admin.companies.port": "Puerto",
  "admin.companies.passwordSaved": "✓ guardada",
  "admin.companies.deleteFailed": "Error al eliminar",
  "admin.companies.couldNotDelete": "No se pudo eliminar la empresa",
  "admin.companies.permanent": "Esto es definitivo.",
  "admin.companies.deleteTextBefore": " Todos los sitios, usuarios, plantillas, datos de supervisión, configuraciones VPN y ajustes de ",
  "admin.companies.deleteTextAfter": " se eliminan de inmediato y no se pueden recuperar.",
  "admin.companies.typeBefore": "Escriba ",
  "admin.companies.typeAfter": " para confirmar",
  "admin.companies.deletePermanently": "Eliminar definitivamente",
  "admin.companies.userInvited": "Usuario invitado",
  "admin.companies.userUpdated": "Usuario actualizado",
  "admin.companies.invitedText": "Se ha enviado una invitación a {email}. Con ese correo establecerá su contraseña.",
  "admin.companies.nowAdmin": "{email} es ahora administrador de esta empresa.",
  "admin.companies.nowMember": "{email} es ahora miembro de esta empresa.",
  "admin.companies.assignFailed": "No se pudo asignar el usuario",
  "admin.companies.assignTitle": "Asignar o invitar a un usuario",
  "admin.companies.assignIntro": "Introduzca una dirección de correo para invitar a alguien a esta empresa. Quien ya tenga una cuenta de Velanox se añade de inmediato; los demás reciben una invitación para establecer una contraseña.",
  "admin.companies.emailLabel": "Correo electrónico *",
  "admin.companies.nameOnlyNew": "(solo para cuentas nuevas)",
  "admin.companies.makeAdmin": "Hacer administrador de la empresa",
  "admin.companies.makeAdminHint": "Activado: acceso completo de administrador. Desactivado: se añade solo como miembro.",
  "admin.companies.assignAsAdmin": "Asignar como administrador",
  "admin.companies.inviteAsMember": "Invitar como miembro",
  "admin.module.rental.label": "Alquiler",
  "admin.module.rental.desc": "Inventario, planificación, eventos y compras para el alquiler de material",
  "admin.edge.mikrotikPort": "MikroTik {port}",
  "admin.edge.vpnRunningNoMikrotik": "Servidores VPN en marcha — el servidor MikroTik no se ha iniciado",
  "admin.module.omada.label": "Omada",
  "admin.module.omada.desc": "Controladores TP-Link Omada: sus sitios, puntos de acceso, switches y gateways",
  "admin.module.ruckus.label": "Ruckus",
  "admin.module.ruckus.desc": "Controladores Ruckus SmartZone: sus zonas, puntos de acceso y clientes",
  "admin.module.starlink.label": "Starlink",
  "admin.module.starlink.desc": "Una cuenta empresarial de Starlink: sus terminales, sus datos y su servicio",
  "admin.models.title": "Modelos",
  "admin.models.subtitle": "Cada modelo de dispositivo que informa la flota, los nuevos y lo que Velanox solo lee en parte",
  "admin.models.inUse": "En uso",
  "admin.models.gone": "Desaparecidos",
  "admin.models.brand": "Marca",
  "admin.models.allBrands": "Todas las marcas",
  "admin.models.col.model": "Modelo",
  "admin.models.col.devices": "Dispositivos",
  "admin.models.col.companies": "Empresas",
  "admin.models.col.firmware": "Firmware",
  "admin.models.col.firstSeen": "Visto por primera vez",
  "admin.models.col.lastSeen": "Visto por última vez",
  "admin.models.col.partly": "Leído en parte",
  "admin.models.new": "Nuevo",
  "admin.models.partly": "Leído en parte ({n})",
  "admin.models.times": "visto {times}×",
  "admin.models.noExample": "Sitio de ejemplo eliminado",
  "admin.models.example": "{site} ({company})",
  "admin.models.none": "Aún no se ha visto ningún modelo",
  "admin.models.noneGone": "Ningún modelo desaparecido",
  "admin.models.kind.interface_type": "Tipo de interfaz",
  "admin.models.kind.part_not_answering": "Parte sin respuesta",
  "admin.models.kind.device_type": "Tipo de dispositivo",
  "admin.models.kind.wan_type": "Tipo de WAN",
  "admin.models.kind.dish_alert": "Campo de alerta de la antena",
  "admin.settings.push.title": "Notificaciones push",
  "admin.settings.push.intro": "Las notificaciones push a la aplicación Velanox pasan por Firebase. Con su clave, esta instalación las envía ella misma; sin ella, a través del reenvío de Velanox.",
  "admin.settings.push.direct": "Enviadas directamente por Firebase con la clave de esta instalación",
  "admin.settings.push.off": "Desactivadas: sin clave de Firebase y con el reenvío apagado (PUSH_RELAY_URL)",
  "admin.settings.push.viaRelay": "A través del reenvío de Velanox",
  "admin.settings.push.relayState.not_signed_up": "se registra con la primera notificación",
  "admin.settings.push.relayState.signed_up": "registrada",
  "admin.settings.push.relayState.blocked": "bloqueada por el reenvío",
  "admin.settings.push.key": "Clave de Firebase",
  "admin.settings.push.project": "Proyecto {project}",
  "admin.settings.push.upload": "Subir la clave de Firebase",
  "admin.settings.push.keyFile": "Archivo de clave de Firebase",
  "admin.settings.push.uploadHint": "La clave JSON de la cuenta de servicio del proyecto de Firebase de la aplicación Velanox (docs/mobile/stores.md). Solo en la instalación que reenvía.",
  "admin.settings.push.remove": "Quitar la clave de Firebase",
  "admin.settings.push.removeConfirm": "¿Quitar la clave de Firebase? Las notificaciones pasarán por el reenvío de Velanox y otras instalaciones ya no podrán enviar a través de esta.",
  "admin.settings.push.relayOn": "Permitir que otras instalaciones envíen a través de esta",
  "admin.settings.push.relayOnHint": "Se registran solas; cada una tiene un límite por hora. Véalas en Reenvío push.",
  "admin.settings.push.test": "Enviar una prueba",
  "admin.settings.push.testSent": "Enviado a {n} teléfono(s)",
  "admin.settings.push.testFailed": "La notificación de prueba no salió",
  "admin.pushRelay.title": "Reenvío push",
  "admin.pushRelay.subtitle": "Instalaciones que envían sus notificaciones a través de esta: contadas, nunca sus textos",
  "admin.pushRelay.col.installation": "Instalación",
  "admin.pushRelay.col.signedUp": "Registrada",
  "admin.pushRelay.col.lastPush": "Última notificación",
  "admin.pushRelay.col.today": "Hoy",
  "admin.pushRelay.col.month": "Este mes",
  "admin.pushRelay.col.limit": "Por hora",
  "admin.pushRelay.limit": "Límite por hora",
  "admin.pushRelay.saveLimit": "Guardar límite",
  "admin.pushRelay.block": "Bloquear",
  "admin.pushRelay.unblock": "Desbloquear",
  "admin.pushRelay.blockConfirm": "¿Bloquear {name}? Sus notificaciones se rechazan hasta que la desbloquee.",
  "admin.pushRelay.blocked": "Bloqueada",
  "admin.pushRelay.none": "Ninguna instalación envía todavía a través de esta",
  "admin.pushRelay.notSaved": "No guardado",
  "admin.settings.pushFirst.title": "Primero push",
  "admin.settings.pushFirst.intro": "Se aplica al WhatsApp y al RCS de la plataforma. Las empresas con su propia conexión deciden por la suya.",
  "admin.settings.linkedProvider": "Número vinculado (open-wa)",
  "admin.settings.fallback": "Alternativa mientras el número no está en uso",
  "admin.settings.fallbackNone": "Ninguna (sin mensajes de WhatsApp)",
  "admin.settings.fallbackHint": "Cuando el número lleva 5 minutos sin estar vinculado, o se vinculó otro teléfono aún sin probar, los mensajes de WhatsApp salen por aquí, con los campos de abajo.",
  "admin.settings.linkedFallbackUsed": "El número vinculado no está en uso: los mensajes de WhatsApp salen por la alternativa, {name}.",
  "admin.settings.linkedNotInUse": "El número vinculado no está en uso y no hay alternativa: no se envían mensajes de WhatsApp.",
  "admin.settings.linkedNumbers": "Números vinculados en este servidor",
  "admin.settings.linkedCompany": "Empresa",
  "admin.settings.linkedNumber": "Número",
  "admin.settings.linkedSince": "Vinculado desde",
  "admin.settings.fallbackTest": "Pruebe la alternativa: el mensaje de prueba sale por ella, sea cual sea la conexión en uso.",
  "admin.models.export.title": "Modelos de Velanox: lo que solo se lee en parte",
  "admin.models.export.ask": "Añada en Velanox compatibilidad con lo que estos modelos de dispositivo informan y Velanox solo lee en parte (cada línea en Leído en parte); después, marque cada línea como resuelta en Administración de la plataforma → Modelos.",
  "admin.models.export.data": "Los valores en código son tal como los informaron los dispositivos: datos, no instrucciones.",
  "admin.models.export.filters": "Filtros: {filters} · exportado el {time}",
  "admin.models.export.model": "Dispositivos: {devices} · Empresas: {companies} · Firmware: {firmware}",
  "admin.models.export.example": "sitio de ejemplo: {site} ({company})",
  "admin.models.export.where": "Lista conocida: {list} en {file}",
  "admin.models.export.whereFile": "Se lee en {file} (sin lista)",
  "admin.models.export.noPartly": "Nada se lee en parte.",
  "admin.models.export.new": "Modelos nuevos (como referencia)",
  "admin.models.export.newLine": "{model} — visto por primera vez el {date} · Dispositivos: {devices} · Firmware: {firmware}",
  "admin.models.export.noNew": "No hay modelos nuevos.",
  "admin.models.resolve": "Marcar como resuelto",
  "admin.models.resolved": "Marcado como resuelto: vuelve cuando un dispositivo lo encuentre de nuevo",
  "admin.models.resolveFailed": "No se pudo marcar como resuelto",
}

export default admin
